/* eslint-disable typescript-enum/no-enum */

/*
 * Everything related to generic firebase types and firebase configuration.
 */

import type { FirebaseApp, FirebaseError, FirebaseOptions } from 'firebase/app';
import type { Auth, User, Persistence } from 'firebase/auth';
import type {
  Database,
  DataSnapshot,
  DatabaseReference,
  ThenableReference,
  EventType,
  OnDisconnect,
  Query as FirebaseQuery,
  QueryConstraint,
  QueryConstraintType,
  TransactionResult,
  TransactionOptions,
} from 'firebase/database';
import type {
  Firestore,
  CollectionReference,
  DocumentData,
  Query as FirestoreQuery,
  WhereFilterOp,
  QueryFieldFilterConstraint,
  QuerySnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import type {
  FirebaseStorage,
  StorageReference,
  UploadTask,
  UploadTaskSnapshot,
} from 'firebase/storage';

import { Optional } from '@vimeo/core/types/generic';

export type TFirebaseApplicationModule = typeof import('firebase/app');

export type TFirebaseDatabaseModule = typeof import('firebase/database');

export type TFirebaseStorageModule = typeof import('firebase/storage');

export type TFirebaseAuthModule = typeof import('firebase/auth');

export type TFirebaseFirestoreModule = typeof import('firebase/firestore');

export type TFirebaseApp = FirebaseApp;

export type TFirebaseOptions = FirebaseOptions;

export type TFirebaseUser = User;

export type TFirebaseEventType = EventType;

export type TFirebasePersistence = Persistence;

export type TFirebaseReference = DatabaseReference;

export type TFirebaseTransactionOptions = TransactionOptions;

export type TFirebaseTransactionResult = TransactionResult;

export type TFirebaseThenableReference = ThenableReference;

export type TFirebaseOnDisconnect = OnDisconnect;

export type TFirebasePrescriber<T = unknown> = [TFirebaseReference, T];

export type TFirebaseQuery = FirebaseQuery;

export type TQueryConstraint = QueryConstraint;

export type TQueryConstraintType = QueryConstraintType;

export type TFirebaseSnapshot<T = unknown> = DataSnapshot & { val(): T };

export type TFirebaseUploadTask = UploadTask;

export type TFirebaseUploadTaskSnapshot = UploadTaskSnapshot;

export type TFirebaseDatabase = Database;

export type TFirebaseStorage = FirebaseStorage;

export type TFirebaseStorageReference = StorageReference;

export type TFirebaseAuth = Auth;

export type TFirebaseError = FirebaseError;

export type TFirestore = Firestore;

export type TDocumentData = DocumentData;

export type TFirestoreQuery = FirestoreQuery;

export type TCollectionReference = CollectionReference<TDocumentData>;

export type TWhereFilterOperator = WhereFilterOp;

export type TQueryFieldFilterConstraint = QueryFieldFilterConstraint;

export type TFirestoreQuerySnapshot = QuerySnapshot;

export type TFirestoreUnsubscribe = Unsubscribe;

export interface IFirebaseApiConfiguration {
  apiKey: string;
  authDomain: string;
  databaseURL: string;
  projectId: string;
  storageBucket: string;
}

export interface IFirebaseDependencies {
  firebase: TFirebaseApplicationModule;
  auth: TFirebaseAuthModule;
  database: TFirebaseDatabaseModule;
  storage: TFirebaseStorageModule;
  firestore: Optional<TFirebaseFirestoreModule>;
}

export enum EFirebasePersistenceType {
  MEMORY = 'MEMORY',
  SESSION = 'SESSION',
  LOCAL_STORAGE = 'LOCAL_STORAGE',
  INDEXED_DB = 'INDEXED_DB',
}
